import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CreditCard,
  Security,
  AccessTime,
  CheckCircle,
} from "@mui/icons-material";
import axios from "axios";

import Api from "../Api/Api.json";
import { setLoading } from "../App";

const environment = process.env.NODE_ENV === "production"
  ? "production"
  : "development";

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cost, setCost] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [patientId, setPatientId] = useState(0);
  const [isPrepopulated, setIsPrepopulated] = useState(false);
  const [shippingName, setShippingName] = useState('');
  const [shippingPhone, setShippingPhone] = useState('');
  const [shippingEmail, setShippingEmail] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [prescriptionData, setPrescriptionData] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    const storedInfo = localStorage.getItem("user_info");
    if (storedInfo !== null) {
      const userInfo = JSON.parse(storedInfo);
      setPatientId(userInfo.di_id);
    }
  }, []);

  useEffect(() => {
    // Prepopulate amount, name, and phone from navigation state if available
    if (location.state) {
      if (location.state.amount) {
        setCost(location.state.amount);
        setIsPrepopulated(true);
      }
      if (location.state.name) {
        setShippingName(location.state.name);
      }
      if (location.state.phone) {
        setShippingPhone(location.state.phone);
      }
      if (location.state.prescription) {
        setPrescriptionData(location.state.prescription);
      }
    }
  }, [location.state]);

  // Validation helper functions
  const validateEmail = (email) => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) {
      return 'Phone number is required';
    }
    const phoneDigits = phone.replace(/\D/g, ''); // Remove all non-digit characters
    if (phoneDigits.length < 9) {
      return 'Phone number should be at least 9 digits';
    }
    return '';
  };

  const createPaymentUpskill = async () => {
    const num = patientId + new Date().getTime();

    const body = {
      task: "shurjopayCreatePayment",
      amount: cost.toString(),
      merchantInvoiceNumber: num.toString(),
      customer_name: shippingName,
      customer_email: shippingEmail,
      customer_city: shippingCity,
      customer_address: shippingAddress,
      customer_phone: shippingPhone,
      environment: environment,
      app: "muktibd"
    };

    try {
      setIsConfirming(true);
      const headers = { "Content-Type": "application/json" };
      const res = await axios.post(Api.BKASH_API, body, { headers });
      console.log(res.data);
      if (!res.data.errorMessage && res.data.message !== "Unauthorized") {
        const matches =
          res.data.customer_order_id.toString() === num.toString() &&
          res.data.amount.toString() === cost.toString() &&
          res.data.customer_name === shippingName &&
          res.data.customer_email === shippingEmail &&
          res.data.customer_city === shippingCity &&
          res.data.customer_address === shippingAddress &&
          res.data.customer_phone === shippingPhone.toString();
        if (matches) {
          console.log("Redirecting to shurjopay payment page");
          // Store prescription data in localStorage for use after payment
          if (prescriptionData) {
            localStorage.setItem('appointment_prescription_data', JSON.stringify(prescriptionData));
          }
          window.location = res.data.checkout_url; // Redirect to shurjopay payment page
        } else {
          console.error("Payment details do not match");
        }
      } else {
        console.error("Unauthorized access or error in payment creation");
      }
    } catch (err) {
      console.error("Error creating payment:", err);
    } finally {
      setIsConfirming(false);
    }
  };

  const handlePayment = () => {
    // Validate all fields
    const emailErr = validateEmail(shippingEmail);
    const phoneErr = validatePhone(shippingPhone);
    
    setEmailError(emailErr);
    setPhoneError(phoneErr);

    if (cost <= 0) {
      alert("Please enter a valid amount");
      return;
    }
    if (!shippingName.trim()) {
      alert("Please enter your name");
      return;
    }
    if (emailErr) {
      return;
    }
    if (!shippingCity.trim()) {
      alert("Please enter your city");
      return;
    }
    if (!shippingAddress.trim()) {
      alert("Please enter your address");
      return;
    }
    if (phoneErr) {
      return;
    }
    
    // Store shipping info in localStorage for invoice
    localStorage.setItem('shipping_info', JSON.stringify({
      name: shippingName,
      email: shippingEmail,
      city: shippingCity,
      address: shippingAddress,
      phone: shippingPhone,
      amount: cost,
      timestamp: new Date().toISOString()
    }));
    createPaymentUpskill();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-4 px-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        {/* Main Payment Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <CreditCard style={{ width: 24, height: 24 }} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Secure Payment</h2>
                <p className="text-blue-100 text-sm">
                  Complete your transaction
                </p>
              </div>
            </div>
          </div>

          {/* Payment Content */}
          <div className="p-6 space-y-6">
            {/* Amount Input */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Payment Amount
                {isPrepopulated && (
                  <span className="ml-2 text-xs text-gray-500 font-normal">(Pre-filled)</span>
                )}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  ৳
                </span>
                <input
                  type="number"
                  value={cost || ''}
                  onChange={(e) => !isPrepopulated && setCost(Number(e.target.value))}
                  readOnly={isPrepopulated}
                  disabled={isPrepopulated}
                  className={`w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl transition-all duration-200 text-lg font-semibold ${
                    isPrepopulated 
                      ? 'bg-gray-100 cursor-not-allowed text-gray-700' 
                      : 'focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                  }`}
                  placeholder="0.00"
                  min="0"
                  step="1"
                />
              </div>
            </div>

            {/* Security Features */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <Security style={{ width: 20, height: 20, color: "#16a34a" }} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Secure Payment
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Your payment is protected with bank-level security and
                    encryption
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={shippingName}
                  onChange={(e) => setShippingName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={shippingEmail}
                  onChange={(e) => {
                    setShippingEmail(e.target.value);
                    if (e.target.value.trim()) {
                      setEmailError(validateEmail(e.target.value));
                    } else {
                      setEmailError('');
                    }
                  }}
                  onBlur={(e) => {
                    setEmailError(validateEmail(e.target.value));
                  }}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    emailError ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email address"
                  required
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={shippingCity}
                  onChange={(e) => setShippingCity(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your city"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="Enter your complete address"
                  rows="3"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={shippingPhone}
                  onChange={(e) => {
                    setShippingPhone(e.target.value);
                    if (e.target.value.trim()) {
                      setPhoneError(validatePhone(e.target.value));
                    } else {
                      setPhoneError('');
                    }
                  }}
                  onBlur={(e) => {
                    setPhoneError(validatePhone(e.target.value));
                  }}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    phoneError ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your phone number (at least 9 digits)"
                  required
                />
                {phoneError && (
                  <p className="text-red-500 text-sm mt-1">{phoneError}</p>
                )}
              </div>
            </div>

            {/* Payment Summary */}
            {cost > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Amount:</span>
                  <span className="text-lg font-bold text-blue-600">
                    ৳{cost.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Payment Button */}
            <button
              onClick={handlePayment}
              disabled={
                isConfirming || 
                cost <= 0 || 
                !shippingName.trim() || 
                !shippingEmail.trim() || 
                emailError ||
                !shippingCity.trim() || 
                !shippingAddress.trim() || 
                !shippingPhone.trim() ||
                phoneError
              }
              className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all duration-300 transform ${
                isConfirming || 
                cost <= 0 || 
                !shippingName.trim() || 
                !shippingEmail.trim() || 
                emailError ||
                !shippingCity.trim() || 
                !shippingAddress.trim() || 
                !shippingPhone.trim() ||
                phoneError
                  ? "bg-gray-400 cursor-not-allowed scale-95"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 shadow-lg hover:shadow-xl"
              }`}
            >
              {isConfirming ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Processing Payment...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <CreditCard style={{ width: 20, height: 20 }} />
                  <span>Pay with Shurjopay</span>
                </div>
              )}
            </button>

            {/* Payment Steps Indicator */}
            {isConfirming && (
              <div className="space-y-3 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-3 text-sm">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <CheckCircle style={{ width: 16, height: 16, color: "white" }} />
                  </div>
                  <span className="text-gray-600">
                    Validating payment details
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <div className="w-6 h-6 border-2 border-blue-600 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  </div>
                  <span className="text-gray-600">
                    Connecting to payment gateway
                  </span>
                </div>
                <div className="flex items-center space-x-3 text-sm opacity-50">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full flex items-center justify-center">
                    <AccessTime style={{ width: 16, height: 16, color: "#9ca3af" }} />
                  </div>
                  <span className="text-gray-400">Redirecting to ShurjoPay</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>Powered by ShurjoPay • Secure Payment Gateway</p>
          <p className="mt-1">Your transaction is protected and encrypted</p>
        </div>
      </div>
    </div>
  );
};

export default Payment;
